import { useEffect, useId, useRef } from 'react';
import { isTextComposition } from './keyboard';
import type { DataTableProps, TableColumn, TableRow } from './table-types';

function nextCell(props: DataTableProps, row: TableRow, column: TableColumn, backwards: boolean) {
  const currentRow = props.rows.find((entry) => entry.id === row.id);
  const currentColumn = props.columns.find((field) => field.id === column.id);
  if (!currentRow || !currentColumn || !props.canEdit?.(currentRow, currentColumn)) return;
  const cells = props.rows.flatMap((entry) =>
    props.columns
      .filter((field) => props.canEdit?.(entry, field))
      .map((field) => ({ rowId: entry.id, columnId: field.id })),
  );
  const index = cells.findIndex((cell) => cell.rowId === row.id && cell.columnId === column.id);
  return index < 0 ? undefined : cells[index + (backwards ? -1 : 1)];
}

export function TableEditor({
  props,
  row,
  column,
}: {
  props: DataTableProps;
  row: TableRow;
  column: TableColumn;
}) {
  const errorId = useId();
  const saving = useRef(false);
  const current = useRef(props);
  current.current = props;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const edit = props.edit!;
  const save = async (advance?: boolean, backwards = false) => {
    if (saving.current || edit.locked || edit.state === 'saving') return;
    saving.current = true;
    try {
      const confirmed = await edit.save();
      if (confirmed && advance && mounted.current && current.current.edit === edit) {
        const latest = current.current;
        const next = nextCell(latest, row, column, backwards);
        if (next) latest.onEdit?.(next.rowId, next.columnId);
      }
    } catch (error) {
      if (mounted.current && current.current.edit === edit) edit.fail(error);
    } finally {
      saving.current = false;
    }
  };
  return (
    <div
      className="pc-field pc-field-md"
      aria-describedby={edit.error ? errorId : undefined}
      onKeyDown={(event) => {
        if (isTextComposition(event)) {
          event.stopPropagation();
          return;
        }
        if (!['Enter', 'Escape', 'Tab'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (edit.locked || edit.state === 'saving' || saving.current) return;
        if (event.key === 'Escape') edit.cancel();
        else void save(event.key === 'Tab', event.shiftKey);
      }}
    >
      {edit.control}
      {edit.error && (
        <span id={errorId} className="pc-form-error" role="alert">
          {edit.error}
        </span>
      )}
    </div>
  );
}
